import { useEffect, useRef, useState } from "react";
import {
  AppBridge,
  buildAllowAttribute,
  PostMessageTransport,
} from "@modelcontextprotocol/ext-apps/app-bridge";
import { ApiError } from "../../api/client";
import { getMe } from "../../api/hub/identity";
import {
  type AppLaunch,
  callAppTool,
  getApp,
  launchApp,
} from "../../api/hub/apps";
import {
  appHostContext,
  localDownload,
  requireApprovedTool,
  safeAppLink,
  validateProxy,
} from "./security";
import "./apps.css";
export type SandboxAppProps = {
  appId: string;
  version?: number;
  config?: Record<string, unknown>;
  layout?: Record<string, unknown>;
  compact?: boolean;
  sharedOnly?: boolean;
};
/** 唯一 Apps 宿主：可信栏在 iframe 外，HTML 与身份从不进入 URL 或作者全局变量。 */
export function SandboxApp(
  { appId, version, config, layout, compact = false, sharedOnly = false }:
    SandboxAppProps,
) {
  const frame = useRef<HTMLIFrameElement>(null),
    container = useRef<HTMLElement>(null),
    [launch, setLaunch] = useState<AppLaunch | null>(null),
    [error, setError] = useState(""),
    [ready, setReady] = useState(false),
    [retry, setRetry] = useState(0),
    [fullscreen, setFullscreen] = useState(false);
  const inputRef = useRef(config ?? layout ?? {});
  inputRef.current = config ?? layout ?? {};
  useEffect(() => {
    let active = true,
      initialized = false,
      bridge: AppBridge | null = null,
      timeout: ReturnType<typeof setTimeout> | undefined,
      poll: ReturnType<typeof setTimeout> | undefined,
      observer: MutationObserver | undefined;
    const abort = new AbortController();
    setLaunch(null);
    setError("");
    setReady(false);
    const cleanup = () => {
      active = false;
      abort.abort();
      clearTimeout(timeout);
      clearTimeout(poll);
      observer?.disconnect();
      if (frame.current) frame.current.src = "about:blank";
      if (bridge) {
        const old = bridge;
        bridge = null;
        void old.teardownResource({}, { timeout: 500 }).catch(() => undefined)
          .finally(() => old.close());
      }
    };
    const fail = (e: unknown) => {
      if (!active) return;
      setError(e instanceof Error ? e.message : "应用加载失败，请重试");
      setLaunch(null);
      setReady(false);
      cleanup();
    };
    void (async () => {
      const [authorized, me] = await Promise.all([
        launchApp(appId, abort.signal, sharedOnly),
        getMe(),
      ]);
      if (!active) return;
      if (sharedOnly && authorized.sharedOnly !== true) {
        throw new Error("服务器未确认共享批准视图，连接已拒绝");
      }
      if (version !== undefined && version !== authorized.version) {
        throw new Error("此组件版本已失效，请刷新个人主页");
      }
      const proxyUrl = validateProxy(authorized, window.location.origin);
      setLaunch(authorized);
      // iframe 已存在，先装监听再设置 src，避免丢失 proxy-ready。
      const proxy = frame.current;
      if (!proxy?.contentWindow) throw new Error("浏览器无法建立应用沙箱");
      bridge = new AppBridge(null, { name: "lingzhi-home", version: "1.0.0" }, {
        serverTools: {},
        openLinks: {},
        downloadFile: {},
        logging: {},
      }, { hostContext: appHostContext() });
      const current = bridge;
      current.oncalltool = async ({ name, arguments: args = {} }) => {
        if (!active || !initialized) {
          throw new Error("应用尚未完成初始化或已停用");
        }
        requireApprovedTool(authorized, name);
        try {
          const result = await callAppTool(
            authorized,
            name,
            args,
            abort.signal,
          );
          if (!active) throw new Error("应用版本已失效");
          return result;
        } catch (e) {
          if (e instanceof ApiError && [401, 403, 409].includes(e.status)) {
            fail(e);
          }
          throw e;
        }
      };
      current.onopenlink = async ({ url }) => {
        if (!active || !initialized) return { isError: true };
        try {
          const target = safeAppLink(url, authorized);
          if (
            !window.confirm(
              `应用「${authorized.manifest.name}」请求打开外部链接：\n${target}\n\n是否继续？`,
            )
          ) return { isError: true };
          if (!active) return { isError: true };
          window.open(target, "_blank", "noopener,noreferrer");
          return {};
        } catch {
          return { isError: true };
        }
      };
      current.ondownloadfile = async ({ contents }) => {
        if (!active || !initialized) return { isError: true };
        try {
          const file = localDownload(contents);
          if (
            !window.confirm(
              `应用「${authorized.manifest.name}」请求下载：\n${file.filename}（${file.blob.size} 字节）\n\n是否保存？`,
            )
          ) return { isError: true };
          if (!active) return { isError: true };
          const url = URL.createObjectURL(file.blob),
            a = document.createElement("a");
          a.href = url;
          a.download = file.filename;
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          return {};
        } catch {
          return { isError: true };
        }
      };
      current.onrequestdisplaymode = async ({ mode }) => {
        if (!active || !initialized) return { mode: "inline" };
        if (mode === "fullscreen") {
          setFullscreen(true);
          return { mode: "fullscreen" };
        }
        setFullscreen(false);
        return { mode: "inline" };
      };
      current.onsandboxready = () => {
        if (active) {
          void current.sendSandboxResourceReady({
            html: authorized.html,
            csp: authorized.effectiveCsp,
            permissions: Object.fromEntries(
              authorized.effectivePermissions.map(
                (p) => [p === "clipboard-write" ? "clipboardWrite" : p, {}],
              ),
            ),
            sandbox: "allow-scripts",
          }).catch(fail);
        }
      };
      current.oninitialized = () => {
        if (!active) return;
        initialized = true;
        clearTimeout(timeout);
        setReady(true);
        void current.sendToolInput({ arguments: inputRef.current }).then(() =>
          current.sendToolResult({ content: [] })
        ).catch(fail);
      };
      await current.connect(
        new PostMessageTransport(proxy.contentWindow, proxy.contentWindow),
      );
      if (!active) {
        await current.close();
        return;
      }
      proxy.src = proxyUrl;
      timeout = setTimeout(
        () =>
          fail(
            new Error("应用初始化超时，请确认 HTML 使用 MCP Apps 协议后重试"),
          ),
        15000,
      );
      observer = new MutationObserver(() => {
        if (active && initialized) current.setHostContext(appHostContext());
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [
          "data-lingzhi-theme",
          "data-lingzhi-tone",
          "class",
          "style",
        ],
      });
      const check = async () => {
        try {
          const state = await getApp(appId, abort.signal, sharedOnly);
          if (!active) return;
          const usable = sharedOnly || state.ownerId !== me.id
            ? state.sharedApprovedVersion
            : state.privateCurrentVersion;
          if (
            !sharedOnly && state.ownerId === me.id &&
            authorized.permissionStatus === "private-restricted" &&
            state.approval?.status === "approved" &&
            state.approval.version === authorized.version
          ) throw new Error("此版本已获得新的批准范围，请重新加载应用");
          if (state.status === "disabled" || usable !== authorized.version) {
            throw new Error(
              state.status === "disabled"
                ? "应用已停用，连接已关闭"
                : "应用版本已变化，旧连接已关闭，请重新打开",
            );
          }
          poll = setTimeout(() => void check(), 1500);
        } catch (e) {
          fail(e);
        }
      };
      poll = setTimeout(() => void check(), 1500);
    })().catch(fail);
    return cleanup;
  }, [appId, version, retry, sharedOnly]);
  useEffect(() => {
    const exit = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", exit);
    return () => window.removeEventListener("keydown", exit);
  }, []);
  return (
    <section
      ref={container}
      className={`apps-sandbox ${compact ? "is-compact" : ""} ${
        fullscreen ? "is-fullscreen" : ""
      }`}
      aria-label="可信应用外框"
    >
      <header className="apps-trusted-frame">
        <div>
          <strong>{launch?.manifest.name ?? "应用"}</strong>
          <span>
            由 {launch?.ownerName ?? "—"} 发布 · 第 {launch?.version ?? "—"} 版
          </span>
          {launch?.sharedOnly && <span>运行全员已批准版本</span>}
          <span>可读：{launch?.approvedTools.join("、") || "无业务数据"}</span>
        </div>
        <button onClick={() => setFullscreen(!fullscreen)}>
          {fullscreen ? "退出全屏" : "全屏打开"}
        </button>
      </header>
      {error && (
        <div className="apps-error" role="alert">
          <p>{error}</p>
          <button onClick={() => setRetry((n) => n + 1)}>重新加载</button>
        </div>
      )}
      {!error && !ready && (
        <p className="apps-loading" role="status">正在建立可信沙箱连接…</p>
      )}
      <iframe
        ref={frame}
        title={launch?.manifest.name ?? "应用沙箱"}
        sandbox="allow-scripts allow-same-origin"
        allow={buildAllowAttribute(
          launch
            ? Object.fromEntries(
              launch.effectivePermissions.map(
                (p) => [p === "clipboard-write" ? "clipboardWrite" : p, {}],
              ),
            )
            : {},
        )}
        referrerPolicy="no-referrer"
        className={error ? "apps-frame-hidden" : ""}
      />
    </section>
  );
}
